import { RootStore } from '@/app/store'
import { Realtime } from 'openai/resources/beta/realtime/realtime.mjs'

export enum VoiceChatState {
  IDLE = 'idle',
  REQUESTING_PERMISSIONS = 'requesting_permissions',
  INITIALIZING = 'initializing',
  CONNECTING = 'connecting',
  ACTIVE = 'active',
  ERROR = 'error',
}

export enum AudioState {
  INACTIVE = 'inactive',
  LISTENING = 'listening',
  SPEAKING = 'speaking',
}

export enum VoiceChatEventType {
  SPEECH_CAPTURED = 'speech_captured', // input_audio_buffer.committed

  USER_TRANSCRIPTION_COMPLETED = 'user_transcription_completed', // "conversation.item.input_audio_transcription.completed"

  ASSISTANT_RESPONSE_STARTED = 'assistant_response_started', // response.content_part.added
  ASSISTANT_RESPONSE_DELTA = 'assistant_response_delta', // response.audio_transcript.delta
  ASSISTANT_RESPONSE_DONE = 'assistant_response_done', // response.content_part.done

  SPEAKING_STARTED = 'speaking_started', // output_audio_buffer.started
  SPEAKING_ENDED = 'speaking_ended', // output_audio_buffer.stopped

  COMPLETION_USAGE = 'completion_usage', // response.done (usage)

  ERROR = 'error',
}

export interface VoiceChatSession {
  id: string
  active: boolean
  startTime: number
  endTime?: number
  rtcToken: string
}

export interface VoiceChatOptions {
  toolId: string
}

export interface VoiceChatStoreInterface {
  rootStore: RootStore
  state: VoiceChatState
  audioState: AudioState
  currentSession: VoiceChatSession | null
  error: string | null

  startSession(options?: VoiceChatOptions): Promise<boolean>
  endSession(): Promise<void>
  resetState(): void

  get isActive(): boolean
  get isInitializing(): boolean
  get isAssistantSpeaking(): boolean
  get isUserSpeaking(): boolean
}

export interface OpenAIRealTimeSessionResponse {
  sessionId: string
  rtcToken: string
  rtcServer: string
  expiresIn: number
}

export interface WebRTCSpeechCapturedEvent {
  type: VoiceChatEventType.SPEECH_CAPTURED
  itemId: string
}

export interface WebRTCUserTranscriptionCompletedEvent {
  type: VoiceChatEventType.USER_TRANSCRIPTION_COMPLETED
  itemId: string
  text: string
}

export interface WebRTCAssistantResponseStartedEvent {
  type: VoiceChatEventType.ASSISTANT_RESPONSE_STARTED
  itemId: string
}

export interface WebRTCAssistantResponseDeltaEvent {
  type: VoiceChatEventType.ASSISTANT_RESPONSE_DELTA
  itemId: string
  delta: string
}

export interface WebRTCAssistantResponseDoneEvent {
  type: VoiceChatEventType.ASSISTANT_RESPONSE_DONE
  itemId: string
  text: string
}

export interface WebRTCSpeakingStartedEvent {
  type: VoiceChatEventType.SPEAKING_STARTED
  itemId: string
}

export interface WebRTCSpeakingEndedEvent {
  type: VoiceChatEventType.SPEAKING_ENDED
  itemId: string
}

export interface WebRTCCompletionUsageEvent {
  type: VoiceChatEventType.COMPLETION_USAGE
  usage: Realtime.ResponseDoneEvent['response']['usage']
}

export interface WebRTCErrorEvent {
  type: VoiceChatEventType.ERROR
  error: string
  details?: unknown
}

export type WebRTCEvent =
  | WebRTCSpeechCapturedEvent
  | WebRTCAssistantResponseStartedEvent
  | WebRTCUserTranscriptionCompletedEvent
  | WebRTCAssistantResponseDeltaEvent
  | WebRTCAssistantResponseDoneEvent
  | WebRTCSpeakingStartedEvent
  | WebRTCSpeakingEndedEvent
  | WebRTCCompletionUsageEvent
  | WebRTCErrorEvent

// Type-safe mapping of event types to their corresponding event interfaces
export interface WebRTCEventMap {
  [VoiceChatEventType.SPEECH_CAPTURED]: WebRTCSpeechCapturedEvent
  [VoiceChatEventType.USER_TRANSCRIPTION_COMPLETED]: WebRTCUserTranscriptionCompletedEvent
  [VoiceChatEventType.ASSISTANT_RESPONSE_STARTED]: WebRTCAssistantResponseStartedEvent
  [VoiceChatEventType.ASSISTANT_RESPONSE_DELTA]: WebRTCAssistantResponseDeltaEvent
  [VoiceChatEventType.ASSISTANT_RESPONSE_DONE]: WebRTCAssistantResponseDoneEvent
  [VoiceChatEventType.SPEAKING_STARTED]: WebRTCSpeakingStartedEvent
  [VoiceChatEventType.SPEAKING_ENDED]: WebRTCSpeakingEndedEvent
  [VoiceChatEventType.COMPLETION_USAGE]: WebRTCCompletionUsageEvent
  [VoiceChatEventType.ERROR]: WebRTCErrorEvent
}
